import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Tooltip } from '@chain-zen/ui'
import Link from 'next/link'
import React from 'react'

import { WORKSPACE_ROUTES } from '../../../constants/routes'
import type { IPublicPostData } from '../../../types/post'
import { formatWeight } from '../../../utils/common'
import { getTruckType } from '../../../utils/shipment'
import { OriginOrDestination } from '../../shared/origin-destination'

const isPostEmpty = (post: IPublicPostData) => {
  return (
    !post?.loadNumber &&
    !post?.origin?.city &&
    !post?.destination?.city &&
    !post?.originAppointment &&
    !post?.destinationAppointment &&
    !post?.truckType &&
    !post?.weight
  )
}

/**
 * Displays the post info for the offer chat details.

 */
export const PostInfo = ({ post, tmsURL }: { post: IPublicPostData; tmsURL?: string }) => {
  const postEmpty = isPostEmpty(post)

  return (
    <Tooltip content="Click to view the details">
      <Link
        href={`${WORKSPACE_ROUTES.POSTS(post.workspace.workspacename ?? '')}/${post.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        <Card>
          <CardHeader>
            <CardTitle>
              <div className="flex justify-between items-center">
                Post info
                {!!tmsURL && (
                  <Link href={tmsURL} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="h-6 text-xs" variant="outline">
                      View in TMS
                    </Button>
                  </Link>
                )}
              </div>
            </CardTitle>
            <CardDescription>{!!post.description && <p>{post.description}</p>}</CardDescription>
          </CardHeader>
          <CardContent>
            {!postEmpty && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-start gap-1">
                  {!!post.loadNumber && (
                    <span className="text-gray-900 dark:text-gray-100 font-medium">Ref # {post.loadNumber}</span>
                  )}

                  <div className="flex flex-wrap items-start gap-1">
                    {!!post.commodity && <Badge variant="secondary">{post.commodity}</Badge>}

                    {post?.truckType?.map((truck, index) => {
                      const truckType = getTruckType(truck)
                      if (!truckType) return null
                      return (
                        <Badge variant="secondary" key={index}>
                          {truckType?.label}
                        </Badge>
                      )
                    })}

                    {!!post.weight?.value && (
                      <Badge variant="secondary">{`${formatWeight(post.weight.value, post.weight.unit)} `}</Badge>
                    )}
                  </div>
                </div>

                <div className="flex justify-between">
                  <OriginOrDestination
                    appointment={post.originAppointment}
                    city={post.origin?.city || null}
                    state={post.origin?.stateCode || null}
                    type="Pick"
                  />
                  <OriginOrDestination
                    appointment={post.destinationAppointment}
                    city={post.destination?.city || null}
                    state={post.destination?.stateCode || null}
                    type="Drop"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </Link>
    </Tooltip>
  )
}
